import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';

interface ChatMessage {
  id: number;
  from: string;
  text: string;
  time: Date;
  isOwn: boolean;
  isSystem?: boolean; // Cambio de estado
}

interface ChatWindow {
  orderReference: string;
  agentName: string;
  messages: ChatMessage[];
  isOpen: boolean;
  isMinimized: boolean;
  unreadCount: number;
  newMessage: string;
}

@Component({
  selector: 'app-notification-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './notification-chat.component.html',
  styleUrl: './notification-chat.component.css',
})
export class NotificationChatComponent implements OnInit, OnDestroy {
  chatWindows: ChatWindow[] = [];
  totalUnread = 0;
  showContactList = false;
  private originalTitle = '';
  private titleInterval: any;
  private mockInterval: any;
  private msgIdCounter = 1;

  // Mock agents and orders
  private mockAgents = ['María García', 'Carlos López', 'Ana Martínez', 'Pedro Ruiz', 'Laura Fernández'];
  private mockOrders = ['ORD-2026-00142', 'ORD-2026-00287', 'ORD-2026-00391', 'ORD-2026-00455', 'ORD-2026-00512'];

  // Pool de contactos para iniciar conversaciones
  allContacts = [
    { name: 'María García', order: 'ORD-2026-00142', role: 'Agente' },
    { name: 'Carlos López', order: 'ORD-2026-00287', role: 'Supervisor' },
    { name: 'Ana Martínez', order: 'ORD-2026-00391', role: 'Agente' },
    { name: 'Pedro Ruiz', order: 'ORD-2026-00455', role: 'Logística' },
    { name: 'Laura Fernández', order: 'ORD-2026-00512', role: 'Agente' },
    { name: 'Javier Sánchez', order: 'ORD-2026-00618', role: 'Pre-sales' },
    { name: 'Elena Torres', order: 'ORD-2026-00723', role: 'Supervisor' },
    { name: 'Diego Morales', order: 'ORD-2026-00834', role: 'Agente' },
  ];
  private mockMessages = [
    'Hola, ¿tienes novedades del vehículo?',
    'El cliente ha preguntado por la fecha de entrega',
    'Necesito confirmar los datos del contrato',
    'La financiera ha aprobado la operación',
    '¿Puedes revisar los documentos adjuntos?',
    'El proveedor confirmó la fecha de fabricación',
    'Hay una incidencia con la matrícula',
    'El vehículo ya está en concesión',
    'Se requiere la firma del cliente para avanzar',
    'La entrega está programada para la semana que viene',
    'He subido los documentos que faltaban',
    'El seguro ya está tramitado',
    '¿Podemos agendar una llamada con el cliente?',
    'La transferencia del vehículo está en proceso',
  ];

  private stageTransitions = [
    { from: 'Contrato enviado', to: 'Riesgo iniciado' },
    { from: 'Riesgo iniciado', to: 'Riesgo aprobado' },
    { from: 'Riesgo aprobado', to: 'Contrato firmado' },
    { from: 'Contrato firmado', to: 'Vehículo pedido' },
    { from: 'Vehículo pedido', to: 'Vehículo en fabricación' },
    { from: 'En fabricación', to: 'En transporte' },
    { from: 'En transporte', to: 'En concesión' },
    { from: 'En concesión', to: 'Facturado' },
    { from: 'Facturado', to: 'Matriculado' },
    { from: 'Matriculado', to: 'Preparado' },
    { from: 'Preparado', to: 'Entregado' },
  ];
  private stageInterval: any;

  constructor(private titleService: Title) {}

  ngOnInit(): void {
    this.originalTitle = this.titleService.getTitle();

    // Create initial chat windows (simulating existing conversations)
    this.createInitialChats();

    // Start simulating incoming messages
    this.mockInterval = setInterval(() => {
      this.simulateIncomingMessage();
    }, 5000 + Math.random() * 8000); // Random between 5-13 seconds

    // Simulate stage changes every 10-20 seconds
    this.stageInterval = setInterval(() => {
      this.simulateStageChange();
    }, 10000 + Math.random() * 10000);

    // Title blink for unread
    this.titleInterval = setInterval(() => {
      this.updateTitle();
    }, 1500);
  }

  ngOnDestroy(): void {
    clearInterval(this.mockInterval);
    clearInterval(this.stageInterval);
    clearInterval(this.titleInterval);
    this.titleService.setTitle(this.originalTitle);
  }

  private createInitialChats(): void {
    // Create 3 existing conversations with history
    for (let i = 0; i < 3; i++) {
      const chat: ChatWindow = {
        orderReference: this.mockOrders[i],
        agentName: this.mockAgents[i],
        messages: [],
        isOpen: false,
        isMinimized: true,
        unreadCount: 0,
        newMessage: '',
      };

      // Add some history
      const historyCount = 2 + Math.floor(Math.random() * 3);
      for (let j = 0; j < historyCount; j++) {
        const isOwn = j % 2 === 0;
        chat.messages.push({
          id: this.msgIdCounter++,
          from: isOwn ? 'Tú' : chat.agentName,
          text: this.mockMessages[Math.floor(Math.random() * this.mockMessages.length)],
          time: new Date(Date.now() - (historyCount - j) * 300000),
          isOwn,
        });
      }
      this.chatWindows.push(chat);
    }
  }

  private simulateIncomingMessage(): void {
    // 60% chance existing chat, 40% new chat
    if (this.chatWindows.length > 0 && Math.random() < 0.6) {
      // Add message to existing chat
      const chat = this.chatWindows[Math.floor(Math.random() * this.chatWindows.length)];
      this.addIncomingMessage(chat);
    } else if (this.chatWindows.length < 5) {
      // Create new chat
      const usedOrders = this.chatWindows.map(c => c.orderReference);
      const availableOrders = this.mockOrders.filter(o => !usedOrders.includes(o));
      const usedAgents = this.chatWindows.map(c => c.agentName);
      const availableAgents = this.mockAgents.filter(a => !usedAgents.includes(a));

      if (availableOrders.length > 0 && availableAgents.length > 0) {
        const chat: ChatWindow = {
          orderReference: availableOrders[0],
          agentName: availableAgents[0],
          messages: [],
          isOpen: false,
          isMinimized: true,
          unreadCount: 0,
          newMessage: '',
        };
        this.chatWindows.push(chat);
        this.addIncomingMessage(chat);
      }
    }
  }

  private simulateStageChange(): void {
    if (this.chatWindows.length === 0) return;
    const chat = this.chatWindows[Math.floor(Math.random() * this.chatWindows.length)];
    const transition = this.stageTransitions[Math.floor(Math.random() * this.stageTransitions.length)];

    chat.messages.push({
      id: this.msgIdCounter++,
      from: 'Sistema',
      text: `Cambio de etapa: ${transition.from} → ${transition.to}`,
      time: new Date(),
      isOwn: false,
      isSystem: true,
    });

    if (!chat.isOpen || chat.isMinimized) {
      chat.unreadCount++;
      this.updateTotalUnread();
    }

    if (!chat.isOpen) {
      chat.isOpen = true;
      chat.isMinimized = true;
    }
  }

  private addIncomingMessage(chat: ChatWindow): void {
    chat.messages.push({
      id: this.msgIdCounter++,
      from: chat.agentName,
      text: this.mockMessages[Math.floor(Math.random() * this.mockMessages.length)],
      time: new Date(),
      isOwn: false,
    });

    if (!chat.isOpen || chat.isMinimized) {
      chat.unreadCount++;
      this.updateTotalUnread();
    }

    // Auto-open if first message in new chat
    if (chat.messages.length === 1) {
      chat.isOpen = true;
      chat.isMinimized = true;
    }
  }

  toggleChat(chat: ChatWindow): void {
    if (!chat.isOpen) {
      chat.isOpen = true;
      chat.isMinimized = false;
    } else {
      chat.isMinimized = !chat.isMinimized;
    }

    if (!chat.isMinimized) {
      chat.unreadCount = 0;
      this.updateTotalUnread();
    }
  }

  closeChat(chat: ChatWindow, event: Event): void {
    event.stopPropagation();
    chat.isOpen = false;
    chat.isMinimized = true;
    chat.unreadCount = 0;
    this.updateTotalUnread();
  }

  toggleContactList(): void {
    this.showContactList = !this.showContactList;
  }

  get inactiveContacts(): any[] {
    const activeOrders = this.chatWindows.filter(c => c.isOpen).map(c => c.orderReference);
    return this.allContacts.filter(c => !activeOrders.includes(c.order));
  }

  startChat(contact: any): void {
    // Check if chat already exists but closed
    let chat = this.chatWindows.find(c => c.orderReference === contact.order);
    if (chat) {
      chat.isOpen = true;
      chat.isMinimized = false;
    } else {
      chat = {
        orderReference: contact.order,
        agentName: contact.name,
        messages: [],
        isOpen: true,
        isMinimized: false,
        unreadCount: 0,
        newMessage: '',
      };
      this.chatWindows.push(chat);
    }
    this.showContactList = false;
  }

  sendMessage(chat: ChatWindow): void {
    if (!chat.newMessage.trim()) return;
    chat.messages.push({
      id: this.msgIdCounter++,
      from: 'Tú',
      text: chat.newMessage.trim(),
      time: new Date(),
      isOwn: true,
    });
    chat.newMessage = '';

    // Simulate reply after 2-4 seconds
    setTimeout(() => {
      this.addIncomingMessage(chat);
    }, 2000 + Math.random() * 2000);
  }

  private updateTotalUnread(): void {
    this.totalUnread = this.chatWindows.reduce((sum, c) => sum + c.unreadCount, 0);
  }

  private titleBlinkState = false;
  private updateTitle(): void {
    if (this.totalUnread > 0) {
      this.titleBlinkState = !this.titleBlinkState;
      this.titleService.setTitle(
        this.titleBlinkState
          ? `(${this.totalUnread}) Nuevos mensajes`
          : `(${this.totalUnread}) Digital Buyers Manager`
      );
    } else {
      this.titleService.setTitle('Digital Buyers Manager');
    }
  }

  get openChats(): ChatWindow[] {
    return this.chatWindows.filter(c => c.isOpen);
  }

  get minimizedChatsWithUnread(): ChatWindow[] {
    return this.chatWindows.filter(c => c.isOpen && c.isMinimized && c.unreadCount > 0);
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }

  trackByChat(index: number, chat: ChatWindow): string {
    return chat.orderReference;
  }

  trackByMsg(index: number, msg: ChatMessage): number {
    return msg.id;
  }
}
